import emailjs from '@emailjs/browser';
import { supabase } from '../lib/supabase';

// EmailJS configuration
const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'default_service';
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_default';
const EMAILJS_USER_ID = import.meta.env.VITE_EMAILJS_USER_ID || 'user_xxxxxxxxxxxx';
const SLACK_EMAIL = import.meta.env.VITE_EMAILJS_SLACK_EMAIL || 'leads-aaaahdgij3xgdwhoffnd4fd7uu@one80workspace.slack.com';

interface ContactInfo {
  name: string;
  email: string;
  phone?: string;
  website?: string;
}

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface AISurveyData {
  name: string;
  email: string;
  website: string;
  phone?: string;
  businessType?: string | string[];
  teamSize?: string;
  timeSpenders?: string | string[];
  painPoints?: string;
  aiToolsUsed?: string | string[];
  dailyTools?: string;
  aiComfortLevel?: string;
  aiGoal?: string;
  additionalInfo?: string;
}

async function createCRMContact(formData: ContactFormData) {
  try {
    // Split the name into first and last name
    const nameParts = formData.name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    console.log('Creating CRM contact:', {
      first_name: firstName,
      last_name: lastName,
      email: formData.email
    });

    // Create the contact
    const { data: contact, error: contactError } = await supabase
      .from('crm_contacts')
      .insert([{
        first_name: firstName,
        last_name: lastName,
        email: formData.email,
        phone: formData.phone || null,
        source: 'Website',
        status: 'prospect',
        last_contact_date: new Date().toISOString()
      }])
      .select()
      .single();

    if (contactError) throw contactError;

    // Create a note with the subject and message
    const noteText = `Subject: ${formData.subject}\n\nMessage: ${formData.message}`;
    
    const { error: noteError } = await supabase
      .from('crm_contact_notes')
      .insert([{
        contact_id: contact.id,
        note: noteText
      }]);

    if (noteError) throw noteError;

    return contact;
  } catch (error) {
    console.error('Error creating CRM contact:', error);
    throw error;
  }
}

const sendSurveyNotification = async (contactInfo: ContactInfo): Promise<boolean> => {
  try {
    // Get environment variables
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;

    // Validate environment variables with specific messages
    const missingVars = [];
    if (!publicKey) missingVars.push('VITE_EMAILJS_PUBLIC_KEY');
    if (!serviceId) missingVars.push('VITE_EMAILJS_SERVICE_ID');
    if (!templateId) missingVars.push('VITE_EMAILJS_TEMPLATE_ID');

    if (missingVars.length > 0) {
      console.error(`Missing EmailJS configuration. Please add the following variables to your .env file:

${missingVars.map(v => `${v}=your_${v.toLowerCase()}`).join('\n')}

You can get these values from your EmailJS account:
1. Create an account at https://www.emailjs.com
2. Create an email service
3. Create an email template
4. Get your public key from Account > API Keys
`);
      return false;
    }

    // Initialize EmailJS
    emailjs.init(publicKey);

    // Prepare template parameters
    const templateParams = {
      to_email: 'hello@one80training.com',
      subject: 'New Fractional PM Survey Response',
      from_name: contactInfo.name,
      from_email: contactInfo.email,
      phone: contactInfo.phone || 'Not provided',
      website: contactInfo.website || 'Not provided',
      message: `
New survey response received:

Name: ${contactInfo.name}
Email: ${contactInfo.email}
Phone: ${contactInfo.phone || 'Not provided'}
Website: ${contactInfo.website || 'Not provided'}

This is an automated notification from the One80Labs Survey Application.
      `.trim()
    };

    // Send email
    const response = await emailjs.send(
      serviceId,
      templateId,
      templateParams
    );

    // Check response
    if (response.status === 200) {
      console.log('Email notification sent successfully');
      return true;
    } else {
      console.error('Failed to send email notification:', response);
      return false;
    }
  } catch (error) {
    // Log detailed error information
    if (error instanceof Error) {
      console.error('Error sending email notification:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        cause: error.cause
      });
    } else {
      console.error('Error sending email notification:', error);
    }
    throw error;
  }
};

const sendAISurveyNotification = async (surveyData: AISurveyData): Promise<boolean> => {
  try {
    // Get environment variables
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const aiTemplateId = import.meta.env.VITE_EMAILJS_AI_TEMPLATE_ID;

    // Validate environment variables
    if (!publicKey || !serviceId || !aiTemplateId) {
      console.error('Missing EmailJS configuration for AI survey. Please check your environment variables.');
      return false;
    }

    // Initialize EmailJS
    emailjs.init(publicKey);

    // Format business type array if it exists
    const businessTypes = Array.isArray(surveyData.businessType) 
      ? surveyData.businessType.join(', ') 
      : (surveyData.businessType || 'Not specified');
    
    // Format time spenders array if it exists
    const timeSpenders = Array.isArray(surveyData.timeSpenders) 
      ? surveyData.timeSpenders.join(', ') 
      : (surveyData.timeSpenders || 'Not specified');
    
    // Format AI tools used array if it exists
    const aiToolsUsed = Array.isArray(surveyData.aiToolsUsed) 
      ? surveyData.aiToolsUsed.join(', ') 
      : (surveyData.aiToolsUsed || 'Not specified');

    // Prepare template parameters
    const templateParams = {
      to_email: 'hello@one80training.com',
      subject: 'New AI Audit Survey Response',
      from_name: surveyData.name,
      from_email: surveyData.email,
      website: surveyData.website || 'Not provided',
      phone: surveyData.phone || 'Not provided',
      business_type: businessTypes,
      team_size: surveyData.teamSize || 'Not specified',
      time_spenders: timeSpenders,
      pain_points: surveyData.painPoints || 'Not specified',
      daily_tools: surveyData.dailyTools || 'Not specified',
      ai_tools_used: aiToolsUsed,
      ai_comfort_level: surveyData.aiComfortLevel || 'Not specified',
      ai_goal: surveyData.aiGoal || 'Not specified',
      additional_info: surveyData.additionalInfo || 'Not provided'
    };

    console.log('Sending AI survey with template params:', templateParams);

    // Send email
    const response = await emailjs.send(
      serviceId,
      aiTemplateId,
      templateParams
    );

    // Check response
    if (response.status === 200) {
      console.log('AI survey notification sent successfully');
      return true;
    } else {
      console.error('Failed to send AI survey notification:', response);
      return false;
    }
  } catch (error) {
    // Log detailed error information
    if (error instanceof Error) {
      console.error('Error sending AI survey notification:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        cause: error.cause
      });
    } else {
      console.error('Error sending AI survey notification:', error);
    }
    throw error;
  }
};

export const sendContactFormEmail = async (formData: ContactFormData): Promise<boolean> => {
  try {
    // Get environment variables
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;

    if (!publicKey || !serviceId || !templateId) {
      throw new Error('Missing EmailJS configuration. Please check your environment variables.');
    }

    // Initialize EmailJS
    emailjs.init(publicKey);

    // Skip database operations and focus on sending the email
    console.log('Sending contact form email directly without database operations');

    // Prepare template parameters
    const templateParams = {
      to_email: 'hello@one80training.com',
      subject: `Contact Form: ${formData.subject}`,
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone || 'Not provided',
      message: `
New contact form submission:

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}
Subject: ${formData.subject}

Message:
${formData.message}
      `.trim()
    };

    // Send email
    const response = await emailjs.send(
      serviceId,
      templateId,
      templateParams
    );

    if (response.status !== 200) {
      throw new Error(`Failed to send email: ${response.text}`);
    }

    console.log('Email sent successfully, now attempting to log the submission');
    
    // After successful email, try to log the submission (but don't fail if this doesn't work)
    try {
      // Use the RPC function to process the submission
      console.log('Calling process_contact_form_submission function');
      const { data, error } = await supabase.rpc('process_contact_form_submission', {
        p_name: formData.name,
        p_email: formData.email,
        p_phone: formData.phone || '',
        p_subject: formData.subject,
        p_message: formData.message
      });

      if (error) {
        console.warn('Non-critical: Error processing contact form submission:', error);
      } else {
        console.log('Contact form processed successfully:', data);
      }
    } catch (submissionError) {
      console.warn('Non-critical: Exception processing contact form submission:', submissionError);
    }

    return true;
  } catch (error) {
    console.error('Error sending contact form:', error);
    throw error;
  }
};